import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import type { components } from "../../../types/api.types";
import urls from "../../../urls";
import type { TodoNote } from "../../../utils/backend-types";
import type { NoteAdd, NoteUpdate } from "../TodoModal/Notes/Notes.types";

function fetchNotes(todoId: number) {
  return apiWithoutCamelization
    .get<Array<TodoNote>>(urls.apiWithoutCamelization.todoNotes(todoId))
    .then((response) => response.data);
}

function addNote({ note, internal, as_system_user }: NoteAdd, todoId: number) {
  return apiWithoutCamelization.post<TodoNote>(
    urls.apiWithoutCamelization.todoNoteCreate(),
    {
      note,
      internal,
      as_system_user,
      todo: todoId
    } satisfies components["schemas"]["TodoNoteCreate"]
  );
}

function updateNote({ note_id, note, internal }: NoteUpdate, todoId: number) {
  return apiWithoutCamelization.post<TodoNote>(
    urls.apiWithoutCamelization.todoNoteUpdate(note_id),
    {
      note,
      internal,
      todo: todoId
    } satisfies components["schemas"]["TodoNoteUpdate"] &
      Pick<components["schemas"]["TodoNoteCreate"], "todo">
  );
}

function deleteNote(noteToDelete: number) {
  return apiWithoutCamelization.delete(
    urls.apiWithoutCamelization.todoNotesDetail(noteToDelete)
  );
}

function useNotes({ todoId }: { todoId: number }) {
  const queryClient = useQueryClient();
  function invalidateNotes() {
    queryClient.invalidateQueries({
      queryKey: ["notes", todoId]
    });
  }

  const {
    data: notes,
    isLoading,
    error
  } = useQuery({
    queryKey: ["notes", todoId],
    queryFn: () => fetchNotes(todoId)
  });

  const addMutation = useMutation({
    mutationFn: (noteToAdd: NoteAdd) => addNote(noteToAdd, todoId),
    onSettled: invalidateNotes
  });

  const updateMutation = useMutation({
    mutationFn: (noteToUpdate: NoteUpdate) => updateNote(noteToUpdate, todoId),
    onSettled: invalidateNotes
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNote,
    onSettled: invalidateNotes
  });

  return {
    addNote: addMutation.mutateAsync,
    updateNote: updateMutation.mutateAsync,
    deleteNote: deleteMutation.mutateAsync,
    notes,
    isLoading,
    error
  };
}

export { useNotes };
